import { inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { CommonRoute } from '../enums/common-route.enum';
import { AuthenticationService } from '../services/authentication/authentication.service';
import { PlatformService } from '../services/platform/platform.service';
import { TokenStorageService } from '../services/token-storage/token-storage.service';

@Injectable({ providedIn: 'root' })
export class LoginGuardService implements CanActivate {
  private readonly authenticationService = inject(AuthenticationService);
  private readonly router = inject(Router);
  private readonly platformService = inject(PlatformService);
  private readonly tokenStorage = inject(TokenStorageService);

  canActivate(
    route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> {
    return this.authenticationService.whenInitialized().pipe(
      switchMap(() => this.authenticationService.hasValidSession()),
      map((hasSession) => {
        if (hasSession) {
          // A half-finished sign-in — MFA code still owed, or forced MFA setup
          // not done — is not a session to bounce back to Home from: the header
          // shows "Sign in" for it, and the login page is where it is finished
          // or replaced by a fresh sign-in.
          const payload = this.tokenStorage.decodeToken(
            this.authenticationService.getAccessToken() || '',
          );
          if (payload?.mfaRequired || payload?.mfaSetupRequired) {
            return true;
          }
          // User is already logged in. Redirect them away from the login page.
          if (this.platformService.isAdminUi()) {
            return this.router.createUrlTree([
              route.queryParams['returnUrl'] || CommonRoute.DASHBOARD,
            ]);
          }
          return this.router.createUrlTree([
            route.queryParams['returnUrl'] || CommonRoute.HOME,
          ]);
        }
        // User is not logged in, allow access to the login page.
        return true;
      }),
    );
  }
}

export const LoginGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Observable<boolean | UrlTree> => {
  return inject(LoginGuardService).canActivate(route, state);
};
